import { useEffect, useId, useState } from 'react'
import { Media } from '@/components/Media'
import { trackCta } from '@/lib/analytics'
import type { HubContent } from '@/lib/content.types'

export function Hub({ data }: { data: HubContent }) {
  const [active, setActive] = useState(0)
  const idBase = useId()
  const industries = data.industries

  /**
   * Admin에서 항목을 지우면 개수가 줄어드는데, 하필 선택 중이던 것이 지워지면
   * active 가 범위를 벗어나 **탭도 패널도 아무것도 안 보이는 상태**가 된다.
   * 개수가 바뀔 때마다 범위 안으로 되돌린다.
   */
  useEffect(() => {
    setActive((i) => Math.min(i, Math.max(0, industries.length - 1)))
  }, [industries.length])

  function onTabKeyDown(e: React.KeyboardEvent) {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
    e.preventDefault()
    const dir = e.key === 'ArrowRight' ? 1 : -1
    const next = (active + dir + industries.length) % industries.length
    setActive(next)
    document.getElementById(`${idBase}-tab-${next}`)?.focus()
  }

  return (
    <section id="hub" className="relative px-5 py-20 md:px-10 md:py-[104px]">
      {/* 모바일에서는 전체 중앙 정렬, md 이상에서는 디자인대로 좌측 정렬 */}
      <div className="mx-auto max-w-content text-center md:text-left">
        <div className="mb-8 flex flex-col items-center gap-5 md:flex-row md:flex-wrap md:items-end md:justify-between">
          <div>
            <div className="annx-eyebrow mb-3.5">{data.eyebrow}</div>
            <Media
              media={data.logo}
              className="annx-section-logo mb-3.5"
            />
            <p className="annx-deck mx-auto max-w-[600px] md:mx-0">{data.deck}</p>
          </div>
          <a
            href={data.learnMore.href}
            onClick={() => trackCta('hub_learn_more', data.learnMore.label, data.learnMore.href)}
            className="annx-learn-more"
          >
            {data.learnMore.label}
          </a>
        </div>

        {/* 탭 — 모바일에서는 중앙 정렬로 줄바꿈 */}
        <div
          role="tablist"
          aria-label="Industry use cases"
          className="mb-[22px] flex flex-wrap justify-center gap-[9px] md:justify-start"
          onKeyDown={onTabKeyDown}
        >
          {industries.map((ind, i) => {
            const selected = i === active
            return (
              <button
                key={ind.id}
                id={`${idBase}-tab-${i}`}
                role="tab"
                type="button"
                aria-selected={selected}
                aria-controls={`${idBase}-panel-${i}`}
                tabIndex={selected ? 0 : -1}
                onClick={() => setActive(i)}
                className={`whitespace-nowrap rounded-pill px-4 py-[9px] text-[12.5px] font-semibold transition-all duration-200 ${
                  selected
                    ? 'border border-transparent text-white'
                    : 'border border-line-control bg-transparent text-ink-secondary hover:text-ink-primary'
                }`}
                style={selected ? { background: 'var(--accent)' } : undefined}
              >
                {ind.tabLabel}
              </button>
            )
          })}
        </div>

        <div className="grid items-stretch gap-6 lg:grid-cols-[1.5fr_1fr] lg:gap-[34px]">
          {/* 미디어 스테이지 — 4장이 겹쳐 있다가 크로스페이드 */}
          <div className="annx-media-frame aspect-video">
            {industries.map((ind, i) => (
              <div
                key={ind.id}
                className="absolute inset-0 transition-opacity duration-[550ms] ease-out"
                style={{
                  opacity: i === active ? 1 : 0,
                  pointerEvents: i === active ? 'auto' : 'none',
                }}
                aria-hidden={i !== active}
              >
                <Media media={ind.media} active={i === active} />
              </div>
            ))}
          </div>

          {/* 텍스트 패널 — 모든 항목을 같은 그리드 셀에 겹쳐서
              가장 긴 텍스트 기준으로 높이가 고정되고 탭 전환 시 레이아웃이 흔들리지 않는다 */}
          <div className="flex flex-col justify-center">
            <div className="grid">
              {industries.map((ind, i) => (
                <div
                  key={ind.id}
                  id={`${idBase}-panel-${i}`}
                  role="tabpanel"
                  aria-labelledby={`${idBase}-tab-${i}`}
                  className="[grid-area:1/1] transition-opacity duration-[450ms] ease-out"
                  style={{
                    opacity: i === active ? 1 : 0,
                    pointerEvents: i === active ? 'auto' : 'none',
                  }}
                  aria-hidden={i !== active}
                >
                  <h3 className="mb-3 font-display text-[22px] font-semibold tracking-[-.02em] md:text-[26px]">
                    {ind.title}
                  </h3>
                  <p className="m-0 text-[15.5px] leading-[1.7] text-ink-secondary">{ind.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
